import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ContactModal.css';

// HubSpot Form Configuration
const HUBSPOT_PORTAL_ID = '9439487';
const HUBSPOT_FORM_ID = '3895941c-8448-4fe5-a4d9-96666234d324';

const ContactModal = ({ isOpen, onClose, expertName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: `Hi Shelby, I'd love to know more about booking ${expertName} for:`,
    qwoted_utm_term: '',
    qwoted_utm_source: '',
    qwoted_utm_medium: '',
    qwoted_utm_content: '',
    qwoted_utm_campaign: '',
    landing_page: '',
    ip_address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Extract UTM parameters from URL
      const params = new URLSearchParams(window.location.search);

      // Try URL first, then fallback to cookies, then sessionStorage/localStorage
      const getUtmParam = (param) => {
        // console.log(`[ContactModal] Getting ${param}...`);

        // 1. Check URL params
        const urlValue = params.get(param);
        if (urlValue) {
          // console.log(`[ContactModal] Found ${param} in URL:`, urlValue);
          return urlValue;
        }

        // 2. Check sessionStorage (parent page should set these)
        try {
          const sessionValue = sessionStorage.getItem(param);
          if (sessionValue) {
            // console.log(`[ContactModal] Found ${param} in sessionStorage:`, sessionValue);
            return sessionValue;
          }
        } catch (e) {
          // console.log('Could not access sessionStorage:', e);
        }

        // 3. Check cookies
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
          const [name, value] = cookie.trim().split('=');
          if (name === param) {
            // console.log(`[ContactModal] Found ${param} in cookies:`, value);
            return decodeURIComponent(value);
          }
        }

        // 4. Check if parent sent via postMessage (stored in window object)
        if (window.parentPageData && window.parentPageData[param]) {
          // console.log(`[ContactModal] Found ${param} in window.parentPageData:`, window.parentPageData[param]);
          return window.parentPageData[param];
        }

        // console.log(`[ContactModal] ${param} not found anywhere`);
        return '';
      };

      // console.log('[ContactModal] Modal opened, extracting UTM params...');
      // console.log('[ContactModal] window.parentPageData available:', window.parentPageData);

      // Get landing page from parent page data
      const landingPage = (window.parentPageData && window.parentPageData.page_title) ||
                          sessionStorage.getItem('parent_page_title') ||
                          document.title ||
                          '';

      // Get IP address from parent page data
      const ipAddress = (window.parentPageData && window.parentPageData.ip_address) ||
                        sessionStorage.getItem('ip_address') ||
                        '';

      const utmData = {
        qwoted_utm_term: getUtmParam('utm_term'),
        qwoted_utm_source: getUtmParam('utm_source'),
        qwoted_utm_medium: getUtmParam('utm_medium'),
        qwoted_utm_content: getUtmParam('utm_content'),
        qwoted_utm_campaign: getUtmParam('utm_campaign'),
        landing_page: landingPage,
        ip_address: ipAddress
      };

      // console.log('[ContactModal] Extracted UTM data:', utmData);

      setFormData(prev => ({
        ...prev,
        message: `Hi Shelby, I'd love to know more about booking ${expertName} for:`,
        ...utmData
      }));

      // Reset states when modal opens
      setShowThankYou(false);
      setError('');
    }
  }, [isOpen, expertName]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Debug logging
    // console.log('[ContactModal] Form submission starting...');
    // console.log('[ContactModal] window.parentPageData:', window.parentPageData);
    // console.log('[ContactModal] formData UTMs:', {
    //   qwoted_utm_source: formData.qwoted_utm_source,
    //   qwoted_utm_campaign: formData.qwoted_utm_campaign,
    //   qwoted_utm_medium: formData.qwoted_utm_medium,
    //   qwoted_utm_term: formData.qwoted_utm_term,
    //   qwoted_utm_content: formData.qwoted_utm_content,
    //   landing_page: formData.landing_page
    // });

    // Get page URL - prefer parent page data if available
    const pageUrl = (window.parentPageData && window.parentPageData.page_url) ||
                    sessionStorage.getItem('parent_page_url') ||
                    window.location.href;

    // console.log('[ContactModal] Final pageUrl:', pageUrl);

    // Build HubSpot form submission payload
    const hubspotPayload = {
      fields: [
        { name: 'firstname', value: formData.firstName },
        { name: 'lastname', value: formData.lastName },
        { name: 'email', value: formData.email },
        { name: 'message', value: formData.message },
        { name: 'qwoted_utm_source', value: formData.qwoted_utm_source },
        { name: 'qwoted_utm_medium', value: formData.qwoted_utm_medium },
        { name: 'qwoted_utm_campaign', value: formData.qwoted_utm_campaign },
        { name: 'qwoted_utm_content', value: formData.qwoted_utm_content },
        { name: 'qwoted_utm_term', value: formData.qwoted_utm_term },
        { name: 'landing_page', value: formData.landing_page },
        { name: 'ip_address', value: formData.ip_address }
      ],
      context: {
        pageUri: pageUrl,
        pageName: formData.landing_page
      }
    };

    // console.log('[ContactModal] Sending to HubSpot:', hubspotPayload);

    // Send dataLayer event to parent page for GTM tracking
    try {
      window.parent.postMessage({
        type: 'DATALAYER_PUSH',
        event: 'microsite_form_submit',
        data: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
          expert_name: expertName,
          qwoted_utm_source: formData.qwoted_utm_source,
          qwoted_utm_medium: formData.qwoted_utm_medium,
          qwoted_utm_campaign: formData.qwoted_utm_campaign,
          qwoted_utm_content: formData.qwoted_utm_content,
          qwoted_utm_term: formData.qwoted_utm_term,
          landing_page: formData.landing_page,
          ip_address: formData.ip_address,
          page_url: pageUrl,
          timestamp: new Date().toISOString()
        }
      }, '*');
      // console.log('[ContactModal] DataLayer event sent to parent');
    } catch (err) {
      // console.error('[ContactModal] Could not send dataLayer event:', err);
    }

    try {
      const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(hubspotPayload)
        }
      );

      if (response.ok) {
        setShowThankYou(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          message: '',
          qwoted_utm_term: formData.qwoted_utm_term,
          qwoted_utm_source: formData.qwoted_utm_source,
          qwoted_utm_medium: formData.qwoted_utm_medium,
          qwoted_utm_content: formData.qwoted_utm_content,
          qwoted_utm_campaign: formData.qwoted_utm_campaign,
          landing_page: formData.landing_page,
          ip_address: formData.ip_address
        });

        // Close modal after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        // const errorData = await response.json().catch(() => ({}));
        // console.error('[ContactModal] HubSpot error response:', errorData);
        setError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      // console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="contact-modal-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          &times;
        </button>

        {showThankYou ? (
          <div className="thank-you-message">
            <div className="thank-you-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your message has been sent successfully. We'll get back to you soon!</p>
          </div>
        ) : (
          <>
            <h2>Contact Us About {expertName}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group half">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="First name"
                  />
                </div>
                <div className="form-group half">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder={`Hi Shelby, I'd love to know more about booking ${expertName} for:`}
                />
              </div>

              {error && <div className="error-message">{error}</div>}

              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );

  // Use React Portal to render modal at document body level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ContactModal;
